import { FC, useEffect, useState } from 'react'
import { Box, Checkbox, FormControlLabel, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { NumberInput } from '../../../components/common/table/NumberInput';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../hooks/StoreHooks';
import { setEditedActionData } from '../../../store/reducers/actionModulesReducer';
import { editActionThunk } from '../../../store/thunk/actionsModulesThunk';
import { useActionsData } from '../../../store/selectors/actionModulesSelectors';
import { ModalComponent } from '../../../components/common/ModalComponent';
import DefaultIcon from '../../../components/common/DefaultIcon';
import { IncentivationSourceType, ManualActionIncentivationSourceModel } from '../../../types/actionModule/incentivationSource/incentivationSourceModel';
import { StyledActionEditWrapper, StyledDialogActions, StyledDialogContent, StyledInlineBox, StyledLabel } from './ActionEdit.styles';
import { validateEmail } from '../../../common/logic/EmailValidation';
import TranslatableTextField from '../../../components/common/translations/TranslatableTextField';
import IconAndColorPicker from '../../../common/ui/IconAndColorPicker';
import { GeneralButton } from '../../../common/ui/GeneralButton';
import { ActionModule } from '../../../types/actionModule/actionModule';

interface ActionEditProps {
    actionItem: ActionModule;
    onSaved: () => void;
}

export const ActionEdit: FC<ActionEditProps> = ({ actionItem, onSaved }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const actionsData = useActionsData()
    const [actionItemState, setActionItemState] = useState<ActionModule>(actionItem);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [emailErrors, setEmailErrors] = useState<(string | null)[]>(
        actionItem.emailNotificationAddresses.map(() => null)
    );

    const isEmailValid = emailErrors.every((err) => err === null);

    useEffect(() => {
        setActionItemState(actionItem);
        setEmailErrors(actionItem.emailNotificationAddresses.map(() => null));
    }, [actionItem]);

    const hasDuplicateName = (): boolean => {
        const { name, id } = actionItemState;
        return Boolean(
            actionsData.some(
                (item) => item.id !== id && item.name === name
            )
        );
    };

    const handleEdit = async () => {
        if (hasDuplicateName()) {
            setIsModalOpen(true);
            return;
        }
        const updateInfo: Partial<ActionModule> = { ...actionItemState };
        dispatch(setEditedActionData(updateInfo));
        await dispatch(editActionThunk());
        onSaved();
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
        const email = e.target.value;
        setActionItemState((prev) => {
            const updated = [...prev.emailNotificationAddresses];
            updated[index] = email;
            return { ...prev, emailNotificationAddresses: updated };
        });
        setEmailErrors((prev) => {
            const updated = [...prev];
            updated[index] = email.trim() === '' || validateEmail(email) ? null : t("label.invalidEmail");
            return updated;
        });
    };

    const handleAddEmail = () => {
        setActionItemState((prev) => ({
            ...prev,
            emailNotificationAddresses: [...prev.emailNotificationAddresses, ''],
        }));
        setEmailErrors((prev) => [...prev, null]);
    };

    const handleRemoveEmail = (index: number) => {
        setActionItemState((prev) => {
            const updated = [...prev.emailNotificationAddresses];
            updated.splice(index, 1);
            return { ...prev, emailNotificationAddresses: updated };
        });
        setEmailErrors((prev) => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    };

    const canBeCompletedByEmployee = actionItem.incentivationSource?.type === IncentivationSourceType.ManualActionIncentivationSource &&
        (actionItem.incentivationSource as ManualActionIncentivationSourceModel).canBeCompletedByEmployeeUser;

    return (
        <StyledActionEditWrapper>
            <StyledDialogContent>
                <Stack direction="row" sx={{ alignItems: "center", gap: 2 }}>
                    <DefaultIcon
                        iconName={actionItemState.iconName}
                        colorHex={actionItemState.colorHex}
                        iconUrl={actionItemState.iconUrl} />
                    <Typography variant='h6'>{actionItemState.name}</Typography>
                </Stack>

                <IconAndColorPicker
                    parentId={actionItem.id}
                    parentType="actionModule"
                    iconData={{
                        colorHex: actionItemState.colorHex,
                        iconName: actionItemState.iconName,
                        iconUrl: actionItemState.iconUrl
                    }}
                    uploadDisabled={!actionItem.id}
                    onChange={(updates) => {
                        setActionItemState((prev) => ({
                            ...prev,
                            iconName: updates.iconName,
                            colorHex: updates.colorHex,
                            iconUrl: updates.iconUrl,
                        }));
                    }}
                />

                <TranslatableTextField
                    label={t("actionPage.modulName")}
                    value={actionItemState.name}
                    onChange={(val) => setActionItemState(prev => ({ ...prev, name: val }))}
                    entityType="actionModule"
                    entityId={String(actionItemState.id)}
                    property="name"
                />
                <TranslatableTextField
                    label={t("actionPage.modulDescription")}
                    value={actionItemState.description}
                    onChange={(val) => setActionItemState(prev => ({ ...prev, description: val }))}
                    entityType="actionModule"
                    entityId={String(actionItemState.id)}
                    property="description"
                />
                <TranslatableTextField
                    label={t("actionPage.infoText")}
                    value={actionItemState.employeeDescription}
                    onChange={(val) => setActionItemState(prev => ({ ...prev, employeeDescription: val }))}
                    entityType="actionModule"
                    entityId={String(actionItemState.id)}
                    property="employeeDescription"
                />
                <TranslatableTextField
                    label={t("actionPage.completionDescriptionText")}
                    value={actionItemState.completionDescriptionText || ""}
                    onChange={(val) => setActionItemState(prev => ({ ...prev, completionDescriptionText: val }))}
                    entityType="actionModule"
                    entityId={String(actionItemState.id)}
                    property="completionDescriptionText"
                />

                <StyledInlineBox>
                    <Box sx={{ width: '50%' }}>
                        <StyledLabel variant='subtitle2'>{t("actionPage.reachablePoints")}</StyledLabel>
                        <NumberInput
                            min={0}
                            disabled
                            value={actionItemState.maxReward}
                            onChange={(e, val) => setActionItemState((prev) => ({ ...prev, maxReward: val ?? 0 }))}
                        />
                    </Box>
                    {canBeCompletedByEmployee && (
                        <Box sx={{ width: '50%' }}>
                            <StyledLabel variant='subtitle2'>{t("actionPage.maxUsers")}</StyledLabel>
                            <Stack direction="row" sx={{ alignItems: "center" }}>
                                <FormControlLabel
                                    control={<Checkbox checked={actionItemState.maxUserCanComplete === null} />}
                                    label={t("actionPage.noLimit")}
                                    onChange={(e, val) => setActionItemState({
                                        ...actionItemState,
                                        maxUserCanComplete: val ? null : 1
                                    })}
                                />
                                {actionItemState.maxUserCanComplete !== null && (
                                    <NumberInput
                                        min={1}
                                        value={actionItemState.maxUserCanComplete}
                                        disabled={actionItemState.maxUserCanComplete === null}
                                        onChange={(e, val) => setActionItemState({
                                            ...actionItemState,
                                            maxUserCanComplete: val
                                        })}
                                    />
                                )}
                            </Stack>
                        </Box>
                    )}
                </StyledInlineBox>

                {canBeCompletedByEmployee && (
                    <>
                        <StyledLabel variant='subtitle2'>{t("actionPage.emailNotificationAddress")}</StyledLabel>
                        {actionItemState.emailNotificationAddresses.map((email, index) => (
                            <Stack key={index} direction="row" spacing={1} sx={{ alignItems: "flex-start", mb: 1 }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={email}
                                    error={Boolean(emailErrors[index])}
                                    helperText={emailErrors[index]}
                                    onChange={(e) => handleEmailChange(e, index)}
                                />
                                <IconButton onClick={() => handleRemoveEmail(index)} sx={{ mt: 0.5 }}>
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        ))}
                        <Box sx={{ mb: 1 }}>
                            <GeneralButton appVariant="ghost" onClick={handleAddEmail}>
                                <AddIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                {t("actionPage.addEmailAddress")}
                            </GeneralButton>
                        </Box>
                        <Box sx={{ display: "flex" }}>
                            <Box sx={{ width: '50%' }}>
                                <FormControlLabel
                                    control={<Checkbox checked={actionItemState.summaryEmailNotification} />}
                                    label={t("actionPage.completionSummaryReport")}
                                    onChange={(e, val) => setActionItemState((prev) => ({ ...prev, summaryEmailNotification: val }))}
                                />
                            </Box>
                            <Box sx={{ width: '50%' }}>
                                <FormControlLabel
                                    control={<Checkbox checked={actionItemState.detailedEmailNotification} />}
                                    label={t("actionPage.completionDetailedReport")}
                                    onChange={(e, val) => setActionItemState((prev) => ({ ...prev, detailedEmailNotification: val }))}
                                />
                            </Box>
                        </Box>
                    </>
                )}
            </StyledDialogContent>

            <StyledDialogActions>
                <GeneralButton
                    appVariant="ghost"
                    onClick={() => onSaved()}
                >
                    {t("buttons.cancel")}
                </GeneralButton>
                <GeneralButton
                    onClick={handleEdit}
                    disabled={!isEmailValid}
                >
                    {t("buttons.update")}
                </GeneralButton>
            </StyledDialogActions>

            <ModalComponent isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <Typography variant="body1">{t("actionPage.nameDuplication")}</Typography>
            </ModalComponent>
        </StyledActionEditWrapper>
    )
}